import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Menu,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";
import RestoreOutlinedIcon from "@mui/icons-material/RestoreOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import ForwardToInboxOutlinedIcon from "@mui/icons-material/ForwardToInboxOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { toast } from "react-toastify";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import getTenantIdFromURL from "./utils/getTenantIdFromURL";
import { getCurrentUserId } from "./authUtils";
import { createQuickTask, listQuickTaskAssignees } from "./api/quickTasksApi";
import {
  getPaymentPlatformEntity,
  getPaymentPlatformFullUnderwritingJob,
  getPaymentPlatformOfferLift,
  startPaymentPlatformOfferLift,
  createPaymentPlatformOfferLiftInvitation,
  reviewPaymentPlatformEntity,
  setPaymentPlatformEntityArchived,
  startPaymentPlatformFullUnderwriting,
} from "./api/companyApi";

const C = {
  ink: "#0b2a4a",
  blue: "#1e6fe8",
  muted: "#64748b",
  line: "#e3ebf3",
  green: "#12a150",
  amber: "#d97706",
  red: "#dc2626",
  bg: "#f6f9fc",
};
const money = (value) =>
  value == null
    ? "—"
    : `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const valueText = (value) => {
  if (value == null || value === "") return "—";
  if (typeof value === "number")
    return value.toLocaleString(undefined, { maximumFractionDigits: 3 });
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.length ? value.join(", ") : "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};
const label = (key) =>
  String(key || "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
const color = (decision) =>
  decision === "approve"
    ? C.green
    : decision === "review"
      ? C.amber
      : decision === "decline"
        ? C.red
        : C.muted;

const decisionPresentation = {
  approved: { label: "APPROVED", color: C.green, severity: "success" },
  not_approved: { label: "NOT APPROVED", color: C.red, severity: "error" },
  approved_with_conditions: {
    label: "APPROVED WITH CONDITIONS",
    color: C.amber,
    severity: "warning",
  },
};

export const clientSafeAiText = (value) => String(value || "")
  .replace(/\b(?:claude|gemini)-[a-z0-9.-]+\b/gi, "CrediArc AI")
  .replace(/\b(?:Claude|Anthropic|Gemini|ChatGPT|OpenAI)\b/gi, "CrediArc AI");

export function extractUnderwritingExplanation(reportMarkdown) {
  const report = String(reportMarkdown || "");
  const decisionSection = report.match(
    /(?:^|\n)#{1,4}\s*(?:1[.)]?\s*)?Decision Summary\s*\n([\s\S]*?)(?=\n#{1,4}\s|$)/i,
  );
  const source = decisionSection?.[1] || "";
  return source
    .replace(/<!--[^]*?-->/g, " ")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/[*_`>#|]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 700);
}

export function normalizeUnderwritingMarkdown(reportMarkdown) {
  const rowsExpanded = String(reportMarkdown || "")
    .replace(/\r\n?/g, "\n")
    .replace(/[ \t]*\|\|[ \t]*/g, "\n");
  const normalized = [];

  rowsExpanded.split("\n").forEach((sourceLine) => {
    const line = sourceLine.trim();
    if (!line) {
      if (normalized.at(-1) !== "") normalized.push("");
      return;
    }
    const numberedHeading = line.match(/^(\d{1,2})([a-z]?)\.\s+([^|]+?)(\s+\|.*)?$/i);
    if (numberedHeading) {
      const [, number, suffix, heading, tableRow] = numberedHeading;
      if (normalized.length && normalized.at(-1) !== "") normalized.push("");
      normalized.push(`${suffix ? "###" : "##"} ${number}${suffix}. ${heading.trim()}`, "");
      if (tableRow) normalized.push(tableRow.trim());
      return;
    }
    normalized.push(line);
  });

  const withTableSpacing = [];
  normalized.forEach((line, index) => {
    const isTableRow = /^\|.*\|?$/.test(line);
    const previousIsTableRow = /^\|.*\|?$/.test(normalized[index - 1] || "");
    if (isTableRow && !previousIsTableRow && withTableSpacing.at(-1) !== "") {
      withTableSpacing.push("");
    }
    if (!isTableRow && previousIsTableRow && line && withTableSpacing.at(-1) !== "") {
      withTableSpacing.push("");
    }
    withTableSpacing.push(line);
  });

  return withTableSpacing.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

const headingText = (children) => React.Children.toArray(children)
  .map((child) => (typeof child === "string" || typeof child === "number" ? String(child) : ""))
  .join(" ")
  .replace(/\s+/g, " ")
  .trim();

const headingId = (value) => `underwriting-${String(value || "section")
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-|-$/g, "")}`;

export function underwritingSectionOutline(reportMarkdown) {
  return normalizeUnderwritingMarkdown(reportMarkdown)
    .split("\n")
    .map((line) => line.match(/^##\s+(.+)$/)?.[1]?.trim())
    .filter((title) => /^SECTION\s+\d+\b/i.test(String(title || "").replace(/[*_`]/g, "").trim()))
    .map((title) => ({ title, id: headingId(title) }));
}

export function underwritingReportMeta(reportMarkdown) {
  const text = markdownCellText(reportMarkdown);
  return {
    mode: text.match(/\bMODE:\s*(.+?)(?=\s+DECISION:|$)/i)?.[1]?.trim() || null,
    decision: text.match(/\bDECISION:\s*(.+?)(?=\s+CONFIDENCE:|$)/i)?.[1]?.trim() || null,
    confidence: text.match(/\bCONFIDENCE:\s*([^\n(]+?)(?=\s*\(|\n|$)/i)?.[1]?.trim() || null,
  };
}

function markdownCellText(value) {
  return String(value || "")
    .replace(/<!--[^]*?-->/g, " ")
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    .replace(/[*_`>#]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function tableCells(line) {
  const trimmed = String(line || "").trim().replace(/^\|/, "").replace(/\|$/, "");
  return trimmed.split("|").map(markdownCellText);
}

function numericRecommendationValue(value) {
  const match = String(value || "").match(/-?\d[\d,]*(?:\.\d+)?/);
  if (!match) return null;
  const parsed = Number(match[0].replaceAll(",", ""));
  return Number.isFinite(parsed) ? parsed : null;
}

export function extractSectionElevenRecommendation(reportMarkdown, preferredTier = null) {
  const lines = normalizeUnderwritingMarkdown(reportMarkdown).split("\n");
  const start = lines.findIndex((line) => /^#{1,4}\s*11(?:[.)]|\s)/i.test(line.trim()));
  if (start < 0) return null;
  const relativeEnd = lines.slice(start + 1).findIndex((line) => /^#{1,4}\s*12(?:[.)]|\s)/i.test(line.trim()));
  const sectionLines = lines.slice(start + 1, relativeEnd < 0 ? lines.length : start + 1 + relativeEnd);
  const plainSection = markdownCellText(sectionLines.join("\n"));

  const headerIndex = sectionLines.findIndex((line) => {
    if (!line.trim().startsWith("|")) return false;
    const headers = tableCells(line).map((cell) => cell.toLowerCase());
    return headers.some((cell) => /^(tier|option|recommendation)$/.test(cell))
      && headers.some((cell) => /(?:advance|funding)?\s*amount/.test(cell));
  });
  if (headerIndex < 0) return null;

  const headers = tableCells(sectionLines[headerIndex]).map((cell) => cell.toLowerCase());
  const tierIndex = headers.findIndex((cell) => /^(tier|option|recommendation)$/.test(cell));
  const amountIndex = headers.findIndex((cell) => /(?:advance|funding)?\s*amount/.test(cell));
  const holdbackIndex = headers.findIndex((cell) => /holdback/.test(cell));
  const rows = [];
  for (const line of sectionLines.slice(headerIndex + 1)) {
    if (!line.trim().startsWith("|")) {
      if (rows.length) break;
      continue;
    }
    const cells = tableCells(line);
    if (cells.every((cell) => /^:?-{3,}:?$/.test(cell))) continue;
    const tierCell = cells[tierIndex] || "";
    const tierMatch = tierCell.match(/\b(conservative|moderate|aggressive)\b/i);
    if (!tierMatch) continue;
    rows.push({
      chosenTier: tierMatch[1][0].toUpperCase() + tierMatch[1].slice(1).toLowerCase(),
      amount: numericRecommendationValue(cells[amountIndex]),
      holdbackPercent: holdbackIndex >= 0 ? numericRecommendationValue(cells[holdbackIndex]) : null,
      markedRecommended: /recommended|selected|chosen/i.test(tierCell),
    });
  }
  if (!rows.length) return null;

  const explicitTier = plainSection.match(/(?:final\s+)?recommendation(?:\s+tier)?\s*:?\s*(?:is\s+)?(conservative|moderate|aggressive)\b/i)?.[1];
  const desiredTier = explicitTier || preferredTier;
  const selected = rows.find((row) => desiredTier && row.chosenTier.toLowerCase() === String(desiredTier).toLowerCase())
    || rows.find((row) => row.markedRecommended);
  if (!selected) return null;

  let decision = "approved_with_conditions";
  if (/\b(not approved|declin(?:e|ed)|no offer|do not fund)\b/i.test(plainSection)) decision = "not_approved";
  else if (/\bapproved\b/i.test(plainSection) && !/\b(conditions?|conditional|indicative|stipulations?)\b/i.test(plainSection)) decision = "approved";
  return { ...selected, decision, source: "section_11" };
}

const underwritingMarkdownComponents = {
  h1: ({ node, ...props }) => (
    <Typography component="h1" {...props} sx={{ color: C.ink, fontSize: { xs: 24, md: 30 }, fontWeight: 950, letterSpacing: "-0.025em", mt: 0, mb: 2 }} />
  ),
  h2: ({ node, children, ...props }) => {
    const title = headingText(children);
    return <Typography id={headingId(title)} component="h2" {...props} sx={{ color: C.ink, fontSize: { xs: 18, md: 20 }, fontWeight: 950, letterSpacing: "-0.015em", mt: 4, mb: 1.25, pb: 1, borderBottom: `1px solid ${C.line}`, scrollMarginTop: 20 }}>{children}</Typography>;
  },
  h3: ({ node, ...props }) => (
    <Typography component="h3" {...props} sx={{ color: "#164e86", fontSize: 16, fontWeight: 900, mt: 2.75, mb: 1 }} />
  ),
  p: ({ node, ...props }) => (
    <Typography component="p" {...props} sx={{ color: "#29445f", fontSize: 14.5, lineHeight: 1.78, my: 1.15 }} />
  ),
  ul: ({ node, ...props }) => <Box component="ul" {...props} sx={{ color: "#29445f", pl: 3, my: 1.25, "& li": { pl: 0.5, mb: 0.7, lineHeight: 1.65 } }} />,
  ol: ({ node, ...props }) => <Box component="ol" {...props} sx={{ color: "#29445f", pl: 3, my: 1.25, "& li": { pl: 0.5, mb: 0.7, lineHeight: 1.65 } }} />,
  blockquote: ({ node, ...props }) => <Box component="blockquote" {...props} sx={{ m: "18px 0", px: 2, py: 1, bgcolor: "#f4f8fd", borderLeft: `4px solid ${C.blue}`, borderRadius: "0 10px 10px 0", "& p": { m: 0 } }} />,
  table: ({ node, ...props }) => (
    <Box sx={{ my: 2, overflowX: "auto", border: `1px solid ${C.line}`, borderRadius: 2.5, boxShadow: "0 8px 24px rgba(17, 50, 83, 0.05)" }}>
      <Box component="table" {...props} sx={{ width: "100%", minWidth: 620, borderCollapse: "separate", borderSpacing: 0 }} />
    </Box>
  ),
  thead: ({ node, ...props }) => <Box component="thead" {...props} sx={{ bgcolor: "#edf4fc" }} />,
  th: ({ node, ...props }) => <Box component="th" {...props} sx={{ color: C.ink, px: 1.5, py: 1.25, textAlign: "left", fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: "0.035em", borderBottom: `1px solid ${C.line}`, whiteSpace: "nowrap" }} />,
  td: ({ node, ...props }) => <Box component="td" {...props} sx={{ color: "#29445f", px: 1.5, py: 1.15, fontSize: 13, lineHeight: 1.5, verticalAlign: "top", borderBottom: `1px solid ${C.line}`, "tr:last-of-type &": { borderBottom: 0 } }} />,
  a: ({ node, ...props }) => <Box component="a" {...props} target="_blank" rel="noopener noreferrer" sx={{ color: C.blue, fontWeight: 700, overflowWrap: "anywhere" }} />,
  code: ({ node, ...props }) => <Box component="code" {...props} sx={{ bgcolor: "#edf2f7", borderRadius: 1, px: 0.6, py: 0.2, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: "0.88em" }} />,
};

export function buildRecommendationPresentation(entity = {}) {
  entity = entity || {};
  const assessment = entity.assessment || {};
  const recommendation = assessment.recommendation || {};
  const llm = entity.fullUnderwriting?.recommendationSummary || {};
  const sectionEleven = extractSectionElevenRecommendation(
    entity.fullUnderwriting?.reportMarkdown,
    llm.chosenTier,
  ) || {};
  let decision = sectionEleven.decision || llm.decision;
  if (!decisionPresentation[decision]) {
    if (assessment.decision === "approve") {
      decision = recommendation.requiresManualReview
        ? "approved_with_conditions"
        : "approved";
    } else if (
      assessment.decision === "review" &&
      recommendation.amount != null
    ) {
      decision = "approved_with_conditions";
    } else {
      decision = "not_approved";
    }
  }
  const fallbackConditions = [
    ...(assessment.guardrailHits || []),
    ...(assessment.missingFeatures || []).map(
      (feature) => `Verify ${label(feature).toLowerCase()}`,
    ),
  ];
  const conditions = (llm.conditions?.length ? llm.conditions : fallbackConditions)
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .slice(0, 12);
  const maxPaymentShare = Number(recommendation.maxPaymentShare);
  const fallbackHoldback = Number.isFinite(maxPaymentShare)
    ? maxPaymentShare <= 1
      ? maxPaymentShare * 100
      : maxPaymentShare
    : null;
  const explanation =
    llm.shortExplanation ||
    extractUnderwritingExplanation(entity.fullUnderwriting?.reportMarkdown) ||
    `The current assessment produced a ${label(assessment.decision || "unscored").toLowerCase()} result based on ${assessment.coverage || 0}% evidence coverage. Review the detailed report and source evidence before taking a funding action.`;
  return {
    decision,
    ...decisionPresentation[decision],
    amount:
      sectionEleven.amount ??
      llm.recommendedAmount ??
      (["approve", "review"].includes(assessment.decision)
        ? recommendation.amount
        : null),
    holdbackPercent: sectionEleven.holdbackPercent ?? llm.recommendedHoldbackPercent ?? fallbackHoldback,
    creditScore: llm.recommendedCreditScore ?? assessment.score ?? null,
    conditions,
    explanation,
    chosenTier: sectionEleven.chosenTier || llm.chosenTier || null,
  };
}

export function buildScoreExplainability(entity = {}) {
  const assessment = entity?.assessment || {};
  const evidence = (assessment.evidence || []).map((item) => {
    const normalizedValue = Number(item.value);
    const weight = Number(item.weight);
    const contribution = normalizedValue * weight;
    return {
      feature: item.feature,
      normalizedValue,
      weight,
      contribution: Number.isFinite(contribution) ? contribution : 0,
    };
  }).filter((item) => Number.isFinite(item.normalizedValue) && Number.isFinite(item.weight) && item.weight > 0);
  const observedWeight = evidence.reduce((sum, item) => sum + item.weight, 0);
  const weightedPoints = evidence.reduce((sum, item) => sum + item.contribution, 0);
  const evidenceScore = observedWeight > 0 ? (weightedPoints / observedWeight) * 100 : null;
  const coverage = Number(assessment.coverage || 0);
  const configuredWeight = observedWeight > 0 && coverage > 0 ? observedWeight / (coverage / 100) : null;
  const modelScore = assessment.score == null ? null : Number(assessment.score);
  const displayedScore = buildRecommendationPresentation(entity).creditScore;
  const fullUnderwritingOverride = displayedScore != null && modelScore != null
    && Math.abs(Number(displayedScore) - modelScore) >= 0.05;
  return {
    evidence,
    observedWeight,
    weightedPoints,
    evidenceScore,
    configuredWeight,
    coverage,
    modelScore,
    displayedScore,
    ml: assessment.ml?.applied ? assessment.ml : null,
    fullUnderwritingOverride,
    missingFeatures: assessment.missingFeatures || [],
  };
}

export function buildLimitExplainability(entity = {}) {
  const summary = buildRecommendationPresentation(entity);
  const signals = entity?.signals || {};
  const monthlyVolume = signals.averageMonthlyVolume
    ?? signals.avgMonthlyVolume
    ?? signals.monthlyVolume
    ?? signals.averageMonthlyRevenue
    ?? null;
  const requestedAmount = signals.requestedAmount ?? signals.advanceAmount ?? null;
  const sectionEleven = extractSectionElevenRecommendation(
    entity?.fullUnderwriting?.reportMarkdown,
    entity?.fullUnderwriting?.recommendationSummary?.chosenTier,
  );
  const llmAmount = entity?.fullUnderwriting?.recommendationSummary?.recommendedAmount;
  const amountSource = sectionEleven?.amount != null
    ? "Section 11 selected recommendation"
    : llmAmount != null
      ? "Full underwriting recommendation"
      : summary.amount != null
        ? "Configured policy sizing"
        : "Decision gate";
  return {
    amount: summary.amount,
    decision: summary.decision,
    decisionLabel: summary.label,
    decisionColor: summary.color,
    monthlyVolume,
    requestedAmount,
    riskBand: entity?.assessment?.recommendation?.riskBand || null,
    chosenTier: summary.chosenTier,
    holdbackPercent: summary.holdbackPercent,
    amountSource,
    explanation: summary.explanation,
    conditions: summary.conditions,
  };
}

function Metric({ title, value, detail }) {
  return (
    <Card
      variant="outlined"
      sx={{ p: 2, borderRadius: 3, borderColor: C.line }}
    >
      <Typography
        sx={{
          color: C.muted,
          fontSize: 11,
          fontWeight: 900,
          textTransform: "uppercase",
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{ color: C.ink, fontSize: 24, lineHeight: 1.25, fontWeight: 950 }}
      >
        {value}
      </Typography>
      {detail && (
        <Typography sx={{ color: C.muted, fontSize: 12 }}>{detail}</Typography>
      )}
    </Card>
  );
}

function SummaryCell({ title, value, detail, accent }) {
  return (
    <Box sx={{ px: { xs: 1.4, md: 1.75 }, py: 1.3, minWidth: 0, borderRight: { xs: 0, sm: `1px solid ${C.line}` }, "&:last-of-type": { borderRight: 0 } }}>
      <Typography sx={{ color: C.muted, fontSize: 10.5, fontWeight: 800 }}>{title}</Typography>
      <Typography sx={{ color: accent || C.ink, fontSize: 17, lineHeight: 1.35, fontWeight: 850, overflowWrap: "anywhere" }}>{value}</Typography>
      {detail && <Typography sx={{ color: C.muted, fontSize: 10.5 }}>{detail}</Typography>}
    </Box>
  );
}

function HeaderMetric({ title, value, progress }) {
  const normalizedProgress = Number.isFinite(Number(progress))
    ? Math.max(0, Math.min(100, Number(progress)))
    : 0;
  return (
    <Box sx={{ minWidth: 0, flex: "1 1 150px", px: { xs: 0, md: 2.25 }, py: { xs: 1, md: 0.25 }, borderRight: { xs: 0, md: `1px solid ${C.line}` }, "&:first-of-type": { pl: { md: 0 } } }}>
      <Typography sx={{ color: C.muted, fontSize: 10.5, fontWeight: 800 }}>{title}</Typography>
      <Typography sx={{ color: C.ink, fontSize: 18, lineHeight: 1.35, fontWeight: 900 }}>{value}</Typography>
      <Box sx={{ mt: 0.65, width: "100%", maxWidth: 180, height: 4, borderRadius: 99, bgcolor: "#e6ebf2", overflow: "hidden" }}>
        <Box sx={{ width: `${normalizedProgress}%`, height: "100%", borderRadius: "inherit", bgcolor: C.ink }} />
      </Box>
    </Box>
  );
}

function TabIntro({ title, subtitle, action }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 2 }}>
      <Box>
        <Typography component="h2" sx={{ color: C.ink, fontSize: 19, fontWeight: 850, letterSpacing: "-0.015em" }}>{title}</Typography>
        <Typography sx={{ color: C.muted, fontSize: 12.5 }}>{subtitle}</Typography>
      </Box>
      {action}
    </Box>
  );
}
function FieldGrid({ data, empty = "No data was supplied." }) {
  const entries = Object.entries(data || {}).filter(
    ([, value]) => value !== null && value !== undefined && value !== "",
  );
  if (!entries.length)
    return <Typography sx={{ color: C.muted }}>{empty}</Typography>;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)" },
        gap: 1,
      }}
    >
      {entries.map(([key, value]) => (
        <Box
          key={key}
          sx={{
            p: 1.35,
            border: `1px solid ${C.line}`,
            borderRadius: 2,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              color: C.muted,
              fontSize: 10.5,
              fontWeight: 850,
              textTransform: "uppercase",
            }}
          >
            {label(key)}
          </Typography>
          <Typography
            title={valueText(value)}
            sx={{
              color: C.ink,
              fontSize: 13.5,
              fontWeight: 650,
              overflowWrap: "anywhere",
            }}
          >
            {valueText(value)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function DataRows({ data, empty = "No data was supplied." }) {
  const entries = Object.entries(data || {}).filter(([, value]) => value !== null && value !== undefined && value !== "");
  if (!entries.length) return <Typography sx={{ color: C.muted, fontSize: 13 }}>{empty}</Typography>;
  return (
    <Box>
      {entries.map(([key, value]) => (
        <Box key={key} sx={{ display: "grid", gridTemplateColumns: "minmax(120px, 0.8fr) minmax(0, 1.2fr)", gap: 2, alignItems: "center", py: 1.15, borderBottom: `1px solid ${C.line}`, "&:last-child": { borderBottom: 0 } }}>
          <Typography sx={{ color: C.muted, fontSize: 12 }}>{label(key)}</Typography>
          <Typography sx={{ color: C.ink, fontSize: 12.5, fontWeight: 750, textAlign: "right", overflowWrap: "anywhere" }}>{valueText(value)}</Typography>
        </Box>
      ))}
    </Box>
  );
}

function ChecklistRow({ title, detail, status, ready = false }) {
  const tone = ready ? C.green : C.amber;
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "26px minmax(0, 1fr) auto", gap: 1.15, alignItems: "center", py: 1.15, borderBottom: `1px solid ${C.line}`, "&:last-child": { borderBottom: 0 } }}>
      <Box sx={{ width: 24, height: 24, display: "grid", placeItems: "center", borderRadius: "50%", color: tone, bgcolor: `${tone}14`, fontSize: 13, fontWeight: 900 }}>{ready ? "✓" : "!"}</Box>
      <Box><Typography sx={{ color: C.ink, fontSize: 12.5, fontWeight: 750 }}>{title}</Typography><Typography sx={{ color: C.muted, fontSize: 10.5 }}>{detail}</Typography></Box>
      <Typography sx={{ color: C.muted, fontSize: 10.5 }}>{status}</Typography>
    </Box>
  );
}

function flattenEvidence(value, prefix = "", rows = [], depth = 0) {
  if (rows.length >= 180 || depth > 8 || value === null || value === undefined || value === "") return rows;
  if (Array.isArray(value)) {
    if (!value.length && prefix) rows.push([prefix, "None found"]);
    value.forEach((item, index) => flattenEvidence(item, `${prefix} ${index + 1}`.trim(), rows, depth + 1));
    return rows;
  }
  if (typeof value === "object") {
    Object.entries(value).forEach(([key, item]) => {
      if (["retrievedAt", "command", "log"].includes(key)) return;
      flattenEvidence(item, [prefix, label(key)].filter(Boolean).join(" · "), rows, depth + 1);
    });
    return rows;
  }
  rows.push([prefix || "Evidence", value]);
  return rows;
}

function EvidenceDetails({ title, subtitle, data }) {
  const rows = flattenEvidence(data);
  if (!rows.length) return null;
  return (
    <Section title={title} subtitle={subtitle}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 1 }}>
        {rows.map(([name, value], index) => (
          <Box key={`${name}-${index}`} sx={{ p: 1.35, border: `1px solid ${C.line}`, borderRadius: 2, minWidth: 0 }}>
            <Typography sx={{ color: C.muted, fontSize: 10.5, fontWeight: 850, textTransform: "uppercase" }}>{name}</Typography>
            {typeof value === "string" && /^https?:\/\//i.test(value) ? (
              <Typography component="a" href={value} target="_blank" rel="noreferrer" sx={{ color: C.blue, fontSize: 13.5, fontWeight: 700, overflowWrap: "anywhere" }}>{value}</Typography>
            ) : (
              <Typography sx={{ color: C.ink, fontSize: 13.5, fontWeight: 650, overflowWrap: "anywhere" }}>{valueText(value)}</Typography>
            )}
          </Box>
        ))}
      </Box>
    </Section>
  );
}

function validHttpUrl(value) {
  if (typeof value !== "string" || !/^https?:\/\//i.test(value)) return null;
  try {
    return new URL(value).toString();
  } catch (error) {
    return null;
  }
}

function collectEvidenceUrls(value, rows = [], depth = 0) {
  if (depth > 8 || rows.length >= 80 || value == null) return rows;
  if (typeof value === "string") {
    const url = validHttpUrl(value);
    if (url) rows.push(url);
    return rows;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collectEvidenceUrls(item, rows, depth + 1));
    return rows;
  }
  if (typeof value === "object") {
    Object.values(value).forEach((item) => collectEvidenceUrls(item, rows, depth + 1));
  }
  return rows;
}

function collectEvidenceFindings(value, rows = [], depth = 0) {
  if (depth > 8 || rows.length >= 8 || value == null) return rows;
  if (Array.isArray(value)) {
    value.forEach((item) => collectEvidenceFindings(item, rows, depth + 1));
    return rows;
  }
  if (typeof value !== "object") return rows;
  if (typeof value.finding === "string" && value.finding.trim()) {
    rows.push({
      finding: value.finding.trim(),
      sourceName: value.sourceName || value.source || null,
      sourceUrl: validHttpUrl(value.sourceUrl),
      status: value.status || null,
    });
  }
  Object.values(value).forEach((item) => collectEvidenceFindings(item, rows, depth + 1));
  return rows;
}

const SOCIAL_PLATFORMS = ["Instagram", "Facebook", "YouTube", "TikTok", "LinkedIn"];
const SOCIAL_METRIC_KEYS = {
  follower: "Followers", followers: "Followers", followercount: "Followers", followerscount: "Followers",
  following: "Following", followings: "Following", followingcount: "Following", followscount: "Following",
  post: "Posts", posts: "Posts", postcount: "Posts", postscount: "Posts", mediacount: "Posts",
  like: "Likes", likes: "Likes", likecount: "Likes", likescount: "Likes", totallikes: "Likes", heartcount: "Likes",
  view: "Views", views: "Views", viewcount: "Views", viewscount: "Views", totalviews: "Views",
  subscriber: "Subscribers", subscribers: "Subscribers", subscribercount: "Subscribers", subscriberscount: "Subscribers",
  video: "Videos", videos: "Videos", videocount: "Videos", videoscount: "Videos",
  review: "Reviews", reviews: "Reviews", reviewcount: "Reviews", reviewscount: "Reviews",
  rating: "Rating", starrating: "Rating", averagerating: "Rating",
  comment: "Comments", comments: "Comments", commentcount: "Comments", commentscount: "Comments",
  share: "Shares", shares: "Shares", sharecount: "Shares", sharescount: "Shares",
  reaction: "Reactions", reactions: "Reactions", reactioncount: "Reactions", reactionscount: "Reactions",
  impression: "Impressions", impressions: "Impressions", impressioncount: "Impressions", impressionscount: "Impressions",
  connection: "Connections", connections: "Connections", connectioncount: "Connections", connectionscount: "Connections",
  engagementrate: "Engagement rate",
};

function socialPlatformFromText(value) {
  const text = String(value || "");
  return SOCIAL_PLATFORMS.find((name) => new RegExp(`(^|[^a-z])${name}([^a-z]|$)`, "i").test(text)) || null;
}

function socialMetricDisplay(value) {
  if (typeof value === "number" && Number.isFinite(value)) return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
  const text = String(value ?? "").trim();
  if (/^-?\d+(?:\.\d+)?$/.test(text)) return Number(text).toLocaleString(undefined, { maximumFractionDigits: 2 });
  return text;
}

function addSocialMetric(target, platform, name, value) {
  const display = socialMetricDisplay(value);
  if (!platform || !name || !display || display.length > 32) return;
  const row = { name, value: display };
  const metrics = target[platform] || (target[platform] = []);
  if (!metrics.some((item) => item.name === row.name && item.value === row.value)) metrics.push(row);
}

function extractMetricsFromText(text, platform, target) {
  if (!platform || typeof text !== "string") return;
  const metricWords = "followers?|following|posts?|likes?|views?|subscribers?|videos?|reviews?|ratings?|comments?|shares?|reactions?|impressions?|connections?|engagement(?:\\s+rate)?";
  const before = new RegExp(`([~+]?[0-9][0-9,.]*(?:\\.[0-9]+)?[KMBkmb]?%?)\\s*(${metricWords})`, "gi");
  const after = new RegExp(`(${metricWords})\\s*(?::|=|of)?\\s*([~+]?[0-9][0-9,.]*(?:\\.[0-9]+)?[KMBkmb]?%?)`, "gi");
  const pushMatches = (pattern, valueIndex, nameIndex) => {
    let match;
    while ((match = pattern.exec(text))) {
      const key = match[nameIndex].toLowerCase().replace(/[^a-z]/g, "").replace(/s$/, "");
      const name = SOCIAL_METRIC_KEYS[key] || label(match[nameIndex]);
      addSocialMetric(target, platform, name, match[valueIndex]);
    }
  };
  pushMatches(before, 1, 2);
  pushMatches(after, 2, 1);
}

function collectSocialMetrics(value, target = {}, inheritedPlatform = null, depth = 0) {
  if (value == null || depth > 9) return target;
  if (typeof value === "string") {
    extractMetricsFromText(value, socialPlatformFromText(value) || inheritedPlatform, target);
    return target;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collectSocialMetrics(item, target, inheritedPlatform, depth + 1));
    return target;
  }
  if (typeof value !== "object") return target;
  const context = [value.sourceName, value.source, value.sourceUrl, value.url, value.platform, value.channel].filter(Boolean).join(" ");
  const platform = socialPlatformFromText(context) || inheritedPlatform;
  Object.entries(value).forEach(([key, item]) => {
    const itemPlatform = socialPlatformFromText(key) || platform;
    const metricName = SOCIAL_METRIC_KEYS[key.toLowerCase().replace(/[^a-z]/g, "")];
    if (itemPlatform && metricName && ["string", "number"].includes(typeof item)) addSocialMetric(target, itemPlatform, metricName, item);
    if (typeof item === "string") extractMetricsFromText(item, itemPlatform, target);
    if (item && typeof item === "object") collectSocialMetrics(item, target, itemPlatform, depth + 1);
  });
  return target;
}

export function summarizeDigitalSocialEvidence(value = {}, reportText = "") {
  const data = value?.data || {};
  const orchestrator = data.orchestrator || {};
  const orchestratorData = orchestrator.data || {};
  const company = orchestratorData.company || {};
  const sourceResults = Array.isArray(orchestratorData.results) ? orchestratorData.results : [];
  const sourceStatuses = [data.stored, data.orchestrator, data.webResearch]
    .filter((item) => item && item.status);
  const successSources = sourceResults
    .filter((item) => item?.status === "success" && item?.data)
    .map((item) => label(item.source || "Source"));
  const unavailableSourceCount = sourceResults.filter((item) =>
    ["failed", "error", "unavailable"].includes(String(item?.status || "").toLowerCase()),
  ).length;
  const urls = [...new Set(collectEvidenceUrls(value))];
  const socialMetrics = collectSocialMetrics(value);
  String(reportText || "").split(/\r?\n/).forEach((line) => {
    const platform = socialPlatformFromText(line);
    if (platform) extractMetricsFromText(line, platform, socialMetrics);
  });
  const channelRules = [
    ["Website", (url) => url === validHttpUrl(company.companyWebsite) || url === validHttpUrl(company.website)],
    ["Instagram", (url) => /(^|\.)instagram\.com$/i.test(new URL(url).hostname)],
    ["Facebook", (url) => /(^|\.)facebook\.com$/i.test(new URL(url).hostname)],
    ["YouTube", (url) => /(^|\.)youtube\.com$/i.test(new URL(url).hostname)],
    ["TikTok", (url) => /(^|\.)tiktok\.com$/i.test(new URL(url).hostname)],
    ["LinkedIn", (url) => /(^|\.)linkedin\.com$/i.test(new URL(url).hostname) && /\/(company|showcase)\//i.test(new URL(url).pathname)],
  ];
  const channels = [];
  channelRules.forEach(([name, matches]) => {
    const url = urls.find(matches);
    const metrics = socialMetrics[name] || [];
    if (url || metrics.length) channels.push({ name, url: url || null, metrics });
  });
  const findings = collectEvidenceFindings(data.webResearch?.data || {})
    .filter((item, index, rows) => rows.findIndex((candidate) => candidate.finding === item.finding) === index)
    .slice(0, 4);
  const availableSourceCount = sourceStatuses.filter((item) => item?.status === "available").length;
  const companyName = company.name || company.companyName || null;
  const location = [company.address, company.country].filter(Boolean).join(", ") || null;
  return {
    status: value?.status || (availableSourceCount ? "available" : null),
    companyName,
    location,
    lastChecked: orchestratorData.fetchedAt || value?.retrievedAt || orchestrator?.retrievedAt || null,
    availableSourceCount,
    totalSourceCount: sourceStatuses.length,
    successSources: [...new Set(successSources)],
    unavailableSourceCount,
    channels,
    findings,
  };
}

function DigitalSocialEvidenceSummary({ data, reportText }) {
  const summary = summarizeDigitalSocialEvidence(data, reportText);
  const hasContent = summary.status || summary.companyName || summary.channels.length || summary.findings.length;
  if (!hasContent) return null;
  const checkedAt = summary.lastChecked && !Number.isNaN(new Date(summary.lastChecked).getTime())
    ? new Date(summary.lastChecked).toLocaleString()
    : null;
  return (
    <Section
      title="Digital & social evidence"
      subtitle="Concise business-only summary of the usable digital evidence returned for this report."
    >
      <FieldGrid data={{
        status: summary.status === "available" ? "Available" : label(summary.status),
        business: summary.companyName,
        location: summary.location,
        evidenceSources: summary.totalSourceCount
          ? `${summary.availableSourceCount} of ${summary.totalSourceCount} available`
          : null,
        lastChecked: checkedAt,
      }} />
      {summary.channels.length > 0 && (
        <Box sx={{ mt: 1.75 }}>
          <Typography sx={{ color: C.muted, fontSize: 11, fontWeight: 900, textTransform: "uppercase", mb: 0.75 }}>Business channels located</Typography>
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
            {summary.channels.map((channel) => (
              channel.url ? <Chip key={`${channel.name}-${channel.url}`} component="a" clickable href={channel.url} target="_blank" rel="noreferrer" label={channel.name} variant="outlined" sx={{ color: C.blue, fontWeight: 800 }} />
                : <Chip key={channel.name} label={channel.name} variant="outlined" sx={{ color: C.blue, fontWeight: 800 }} />
            ))}
          </Box>
        </Box>
      )}
      {summary.channels.some((channel) => channel.metrics?.length) && (
        <Box sx={{ mt: 1.75 }}>
          <Typography sx={{ color: C.muted, fontSize: 11, fontWeight: 900, textTransform: "uppercase", mb: 0.75 }}>Social audience & engagement</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 1 }}>
            {summary.channels.filter((channel) => channel.metrics?.length).map((channel) => (
              <Paper key={`${channel.name}-metrics`} variant="outlined" sx={{ p: 1.4, borderColor: C.line, borderRadius: 2 }}>
                <Typography component={channel.url ? "a" : "p"} href={channel.url || undefined} target={channel.url ? "_blank" : undefined} rel={channel.url ? "noreferrer" : undefined} sx={{ color: channel.url ? C.blue : C.ink, fontSize: 13, fontWeight: 850, textDecoration: "none" }}>{channel.name}</Typography>
                <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 1 }}>
                  {channel.metrics.map((metric, index) => (
                    <Box key={`${metric.name}-${metric.value}-${index}`} sx={{ minWidth: 82, px: 1, py: 0.75, bgcolor: C.bg, borderRadius: 1.5 }}>
                      <Typography sx={{ color: C.ink, fontSize: 14, fontWeight: 900 }}>{metric.value}</Typography>
                      <Typography sx={{ color: C.muted, fontSize: 9.5, fontWeight: 800, textTransform: "uppercase" }}>{metric.name}</Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>
      )}
      {summary.findings.length > 0 && (
        <Box sx={{ mt: 1.75, display: "grid", gap: 0.75 }}>
          <Typography sx={{ color: C.muted, fontSize: 11, fontWeight: 900, textTransform: "uppercase" }}>Key findings</Typography>
          {summary.findings.map((item, index) => (
            <Typography key={`${item.finding}-${index}`} sx={{ color: C.ink, fontSize: 13.5, lineHeight: 1.55 }}>
              • {item.finding}{item.sourceName ? ` — ${item.sourceName}` : ""}
            </Typography>
          ))}
        </Box>
      )}
      {summary.successSources.length > 0 && (
        <Typography sx={{ color: C.muted, fontSize: 12.5, mt: 1.5 }}>
          Returned data: {summary.successSources.join(", ")}.
        </Typography>
      )}
      {summary.unavailableSourceCount > 0 && (
        <Alert severity="info" sx={{ mt: 1.5 }}>
          {summary.unavailableSourceCount} external {summary.unavailableSourceCount === 1 ? "source was" : "sources were"} unavailable in this run and treated as missing evidence.
        </Alert>
      )}
    </Section>
  );
}

function IdentityVerificationPanel({ entity, assessment }) {
  const confidence = Math.round((entity.match?.confidence || 0) * 100);
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 1.75 }}>
      <Section
        title="Verified business profile"
        subtitle="Best available identity assembled from Wix and external evidence."
      >
        <DataRows data={entity.identity} />
      </Section>
      <Section
        title="Verification summary"
        subtitle="Confidence by verification layer."
      >
        <ChecklistRow title="Company name" detail="Merchant record name available" status="Verified" ready />
        <ChecklistRow title="Website" detail={entity.identity?.website ? "Public domain supplied" : "No website supplied"} status={entity.identity?.website ? "Verified" : "Missing"} ready={Boolean(entity.identity?.website)} />
        <ChecklistRow title="Entity reconciliation" detail={`${confidence}% overall match · ${assessment.coverage || 0}% evidence coverage`} status={confidence >= 80 ? "Verified" : "Review"} ready={confidence >= 80} />
      </Section>
    </Box>
  );
}

function applicationRecord(value, index) {
  if (value && typeof value === "object") {
    return {
      ...value,
      id: value.id || value.applicationId || value.externalId || `Application ${index + 1}`,
      status: value.status || value.outcome || value.decision || "Recorded",
    };
  }
  return { id: String(value), status: "Recorded" };
}

function ApplicationsOutcomesPanel({ entity }) {
  const records = (entity.applicationHistory || entity.applications || []).map(applicationRecord);
  const [selectedId, setSelectedId] = useState(records[0]?.id || "");
  useEffect(() => {
    if (!records.some((record) => record.id === selectedId)) setSelectedId(records[0]?.id || "");
  }, [records, selectedId]);
  const selected = records.find((record) => record.id === selectedId) || records[0] || null;
  const outcomes = entity.outcomes || {};
  const funded = Number(outcomes.funded || 0);
  const notFunded = Number(outcomes.notFunded || 0);
  const events = selected?.events || selected?.history || [];
  const statusColor = (status) => {
    const normalized = String(status || "").toLowerCase();
    if (/funded|approved|performing|complete/.test(normalized)) return C.green;
    if (/declin|default|late|adverse/.test(normalized)) return C.red;
    if (/review|pending|requested/.test(normalized)) return C.amber;
    return C.muted;
  };
  return (
    <Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(3, 1fr)" }, gap: 1.25, mb: 2 }}>
        <Metric title="Applications" value={(entity.applicationCount || records.length || 0).toLocaleString()} />
        <Metric title="Funded" value={funded.toLocaleString()} />
        <Metric title="Not funded" value={notFunded.toLocaleString()} />
      </Box>
      {!records.length ? (
        <Section title="Application history" subtitle="Applications, decisions and observed performance will appear here as one auditable record.">
          <Box sx={{ py: 4, px: 2, textAlign: "center", bgcolor: "#f8fafc", borderRadius: 2.5 }}>
            <Typography sx={{ color: C.ink, fontWeight: 900 }}>No application history linked</Typography>
            <Typography sx={{ color: C.muted, fontSize: 13, mt: 0.5 }}>
              Application identifiers and outcomes have not been supplied for this company.
            </Typography>
          </Box>
        </Section>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "300px minmax(0, 1fr)" }, gap: 2, alignItems: "start" }}>
          <Paper variant="outlined" sx={{ borderColor: C.line, borderRadius: 3, overflow: "hidden" }}>
            <Box sx={{ p: 1.5, borderBottom: `1px solid ${C.line}` }}>
              <Typography sx={{ color: C.ink, fontWeight: 900 }}>Application history</Typography>
              <Typography sx={{ color: C.muted, fontSize: 12 }}>Select a record to inspect its outcome.</Typography>
            </Box>
            {records.map((record) => (
              <Button
                key={record.id}
                onClick={() => setSelectedId(record.id)}
                aria-pressed={record.id === selected?.id}
                sx={{
                  width: "100%",
                  display: "block",
                  textAlign: "left",
                  textTransform: "none",
                  p: 1.5,
                  borderRadius: 0,
                  borderBottom: `1px solid ${C.line}`,
                  bgcolor: record.id === selected?.id ? "#eef4fb" : "#fff",
                  color: C.ink,
                  "&:last-child": { borderBottom: 0 },
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "center" }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 900 }}>{record.id}</Typography>
                  <Chip size="small" label={label(record.status)} sx={{ color: statusColor(record.status), bgcolor: `${statusColor(record.status)}12`, fontWeight: 800 }} />
                </Box>
                <Typography sx={{ color: C.muted, fontSize: 11.5, mt: 0.5 }}>
                  {[record.createdAt && new Date(record.createdAt).toLocaleDateString(), record.requestedAmount != null && money(record.requestedAmount)].filter(Boolean).join(" · ") || "Identifier only"}
                </Typography>
              </Button>
            ))}
          </Paper>
          <Section title={`${selected.id} · ${label(selected.status)}`} subtitle="Application terms, decision and outcome evidence available for this record.">
            <FieldGrid
              data={{
                requestedAmount: selected.requestedAmount,
                fundedAmount: selected.fundedAmount,
                decision: selected.decision,
                status: selected.status,
                factorRate: selected.factorRate,
                holdbackPercent: selected.holdbackPercent,
                submittedAt: selected.createdAt || selected.submittedAt,
                decidedAt: selected.decidedAt,
                fundedAt: selected.fundedAt,
                outcome: selected.outcome,
              }}
              empty="Only the application identifier was supplied."
            />
            {!!events.length && (
              <Box sx={{ mt: 2 }}>
                <Typography sx={{ color: C.ink, fontWeight: 900, mb: 0.5 }}>Activity timeline</Typography>
                {events.map((event, index) => (
                  <Box key={event.id || `${event.type}-${index}`} sx={{ py: 1, borderBottom: `1px solid ${C.line}` }}>
                    <Typography sx={{ color: C.ink, fontWeight: 800 }}>{label(event.type || event.status || "Activity")}</Typography>
                    <Typography sx={{ color: C.muted, fontSize: 12 }}>
                      {[event.actor, event.createdAt && new Date(event.createdAt).toLocaleString()].filter(Boolean).join(" · ")}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Section>
        </Box>
      )}
      {!!Object.keys(outcomes.decisions || {}).length && (
        <Section title="Historical decision outcomes" subtitle="Complete outcome counts supplied for this company.">
          <FieldGrid data={outcomes.decisions} />
        </Section>
      )}
    </Box>
  );
}

function SignalStatsTable({ stats }) {
  const entries = Object.entries(stats || {});
  if (!entries.length) return <Typography sx={{ color: C.muted }}>No signal history was supplied.</Typography>;
  return (
    <Box>
      {entries.map(([key, raw]) => {
        const value = raw && typeof raw === "object" ? raw : { value: raw };
        const count = value.count ?? (value.value != null ? 1 : null);
        const average = value.average ?? value.avg ?? (value.sum != null && Number(count) ? Number(value.sum) / Number(count) : value.value);
        const min = Number(value.min);
        const max = Number(value.max);
        const numericAverage = Number(average);
        const meter = Number.isFinite(numericAverage) && Number.isFinite(min) && Number.isFinite(max) && max > min ? Math.max(5, Math.min(100, ((numericAverage - min) / (max - min)) * 100)) : 72;
        return (
          <Box key={key} sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "170px minmax(120px, 1fr) 150px" }, gap: 1.5, alignItems: "center", py: 1.2, borderBottom: `1px solid ${C.line}`, "&:last-child": { borderBottom: 0 } }}>
            <Typography sx={{ color: C.muted, fontSize: 12 }}>{label(key)}</Typography>
            <Box sx={{ height: 7, borderRadius: 99, bgcolor: "#e7edf4", overflow: "hidden" }}><Box sx={{ width: `${meter}%`, height: "100%", borderRadius: 99, bgcolor: "#5268ad" }} /></Box>
            <Typography sx={{ color: C.ink, fontSize: 11.5, fontWeight: 750, textAlign: { sm: "right" } }}>{valueText(average)}{count != null ? ` · ${count} observed` : ""}</Typography>
          </Box>
        );
      })}
    </Box>
  );
}

function PlatformKpis({ entity }) {
  const signals = entity.signals || {};
  const transactions = signals.transactions ?? signals.transactionCount ?? signals.totalTransactions ?? entity.signalStats?.transactions?.count;
  const monthlyVolume = signals.averageMonthlyVolume ?? signals.avgMonthlyVolume ?? signals.monthlyVolume ?? signals.averageMonthlyRevenue;
  const volatility = signals.turnoverVolatility ?? signals.revenueVolatility ?? signals.volumeVolatility;
  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1.25, mb: 1.75 }}>
      <Metric title="Transactions" value={transactions == null ? "—" : valueText(transactions)} />
      <Metric title="Avg. monthly volume" value={monthlyVolume == null ? "—" : money(monthlyVolume)} />
      <Metric title="Turnover volatility" value={volatility == null ? "—" : `${Number(volatility) <= 1 ? (Number(volatility) * 100).toFixed(1) : Number(volatility).toFixed(1)}%`} />
    </Box>
  );
}

function MonthlyPerformanceTable({ rows = [] }) {
  if (!rows.length)
    return <Typography sx={{ color: C.muted }}>No month-by-month history was supplied.</Typography>;
  const columns = [
    ["month", "Month"],
    ["paymentVolume", "Payment volume"],
    ["transactions", "Transactions"],
    ["disputes", "Disputes"],
    ["logins", "Logins"],
    ["productViews", "Product views"],
  ];
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 720, "th,td": { p: 1.15, borderBottom: `1px solid ${C.line}`, textAlign: "right", fontSize: 13 }, "th:first-of-type,td:first-of-type": { textAlign: "left" }, th: { color: C.muted, fontSize: 11, textTransform: "uppercase" } }}>
        <thead><tr>{columns.map(([, title]) => <th key={title}>{title}</th>)}</tr></thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.month}>
              {columns.map(([key]) => <td key={key}>{key === "paymentVolume" && row[key] != null ? money(row[key]) : valueText(row[key])}</td>)}
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, mb: 1.75, borderColor: C.line, borderRadius: 2.5, boxShadow: "0 8px 24px rgba(17, 50, 83, 0.025)" }}
    >
      <Typography sx={{ color: C.ink, fontSize: 14, fontWeight: 800 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ color: C.muted, fontSize: 11.5, mb: 1.5 }}>
          {subtitle}
        </Typography>
      )}
      {!subtitle && <Box sx={{ mb: 1.5 }} />}
      {children}
    </Paper>
  );
}

function RecommendationSummary({ entity }) {
  const summary = buildRecommendationPresentation(entity);
  const score = Number(summary.creditScore || 0);
  return (
    <Section
      title="Underwriting recommendation"
      subtitle="Modeled result, supporting rationale, and current commercial terms."
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, pb: 1.75, borderBottom: `1px solid ${C.line}` }}>
        <Box sx={{ width: 76, height: 76, flex: "0 0 auto", borderRadius: "50%", display: "grid", placeItems: "center", background: `conic-gradient(${summary.color} 0 ${Math.max(0, Math.min(100, score))}%, #e7edf4 ${Math.max(0, Math.min(100, score))}% 100%)`, position: "relative", "&:after": { content: '""', position: "absolute", inset: 7, borderRadius: "50%", bgcolor: "#fff" } }}>
          <Typography sx={{ position: "relative", zIndex: 1, color: C.ink, fontSize: 21, fontWeight: 850 }}>{summary.creditScore ?? "—"}</Typography>
        </Box>
        <Box>
          <Typography sx={{ color: summary.color, fontSize: 18, fontWeight: 850 }}>{summary.label}</Typography>
          <Typography sx={{ color: C.muted, fontSize: 12 }}>{summary.amount == null ? "No commercial offer generated" : `${money(summary.amount)} recommended`}</Typography>
          {summary.chosenTier && <Typography sx={{ color: C.muted, fontSize: 11, mt: 0.25 }}>Tier · {summary.chosenTier}</Typography>}
        </Box>
      </Box>
      {summary.decision === "approved_with_conditions" && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: "#f5c46b", bgcolor: "#fffbeb" }}>
          <Typography sx={{ color: C.ink, fontWeight: 900, mb: 1 }}>Approval conditions</Typography>
          {summary.conditions.length ? (
            <Box component="ul" sx={{ my: 0, pl: 2.5, color: C.ink }}>
              {summary.conditions.map((condition) => <li key={condition}>{condition}</li>)}
            </Box>
          ) : (
            <Typography sx={{ color: C.muted }}>Review the report stipulations before approval.</Typography>
          )}
        </Paper>
      )}
      <Typography sx={{ color: "#41556b", fontSize: 12.5, lineHeight: 1.65, pt: 1.5 }}>{summary.explanation}</Typography>
      <Box sx={{ mt: 1.5, display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", borderTop: `1px solid ${C.line}` }}>
        <SummaryCell title="Amount" value={summary.amount == null ? "No offer" : money(summary.amount)} />
        <SummaryCell title="Holdback" value={summary.holdbackPercent == null ? "—" : `${Number(summary.holdbackPercent).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`} />
        <SummaryCell title="Score" value={summary.creditScore ?? "—"} detail="of 100" />
      </Box>
    </Section>
  );
}

function ExplanationValue({ label: title, value, detail }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ color: C.muted, fontSize: 10.5, fontWeight: 800 }}>{title}</Typography>
      <Typography sx={{ color: C.ink, fontSize: 15, fontWeight: 900, overflowWrap: "anywhere" }}>{value}</Typography>
      {detail && <Typography sx={{ color: C.muted, fontSize: 10.5, lineHeight: 1.45 }}>{detail}</Typography>}
    </Box>
  );
}

function ScoreExplainability({ entity }) {
  const explanation = buildScoreExplainability(entity);
  const score = explanation.displayedScore ?? explanation.modelScore;
  return (
    <Section title="Why this score" subtitle="Auditable calculation from the evidence available to the scoring model.">
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 1.5 }}>
        <Box>
          <Typography sx={{ color: C.muted, fontSize: 10.5, fontWeight: 850, textTransform: "uppercase" }}>Displayed score</Typography>
          <Typography sx={{ color: C.ink, fontSize: 28, lineHeight: 1.15, fontWeight: 950 }}>{score ?? "—"}<Box component="span" sx={{ color: C.muted, fontSize: 13, fontWeight: 700 }}> / 100</Box></Typography>
        </Box>
        <Chip size="small" variant="outlined" label={`${explanation.coverage}% evidence coverage`} sx={{ fontWeight: 750 }} />
      </Box>
      {explanation.evidence.length ? (
        <>
          <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, borderColor: C.line, borderRadius: 2, bgcolor: "#f8fafc" }}>
            <Typography sx={{ color: C.muted, fontSize: 10.5, fontWeight: 800 }}>MODEL FORMULA</Typography>
            <Typography sx={{ color: C.ink, mt: 0.35, fontSize: 12.5, fontWeight: 800 }}>
              ({explanation.weightedPoints.toFixed(2)} weighted points ÷ {explanation.observedWeight.toFixed(0)} observed weight) × 100 = {explanation.evidenceScore.toFixed(1)}
            </Typography>
            <Typography sx={{ color: C.muted, mt: 0.35, fontSize: 10.5 }}>
              Missing features are excluded from the denominator; coverage shows how much of the configured model was actually observed.
            </Typography>
          </Paper>
          <Box sx={{ display: "grid", gap: 1 }}>
            {explanation.evidence.slice().sort((a, b) => b.weight - a.weight).map((item) => (
              <Box key={item.feature} sx={{ display: "grid", gridTemplateColumns: "minmax(120px, 1fr) 72px 82px", gap: 1, alignItems: "center" }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ color: C.ink, fontSize: 11.5, fontWeight: 800 }}>{label(item.feature)}</Typography>
                  <Box sx={{ mt: 0.45, height: 4, borderRadius: 99, bgcolor: "#e7edf4", overflow: "hidden" }}><Box sx={{ width: `${Math.max(0, Math.min(100, item.normalizedValue * 100))}%`, height: "100%", bgcolor: C.blue }} /></Box>
                </Box>
                <Typography sx={{ color: C.muted, fontSize: 10.5 }}>Weight {item.weight}</Typography>
                <Typography sx={{ color: C.ink, fontSize: 10.5, fontWeight: 850, textAlign: "right" }}>{item.contribution.toFixed(2)} weighted</Typography>
              </Box>
            ))}
          </Box>
          {explanation.ml && (
            <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2, py: 0.25 }}>
              The evidence score was blended with an active ML prediction at {Math.round(Number(explanation.ml.blendWeight || 0) * 100)}% weight to produce the model score of {explanation.modelScore}.
            </Alert>
          )}
          {explanation.fullUnderwritingOverride && (
            <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2, py: 0.25 }}>
              Full underwriting set the displayed score to {explanation.displayedScore}; the evidence model baseline was {explanation.modelScore}. See the score bridge in the full report for its adjustments.
            </Alert>
          )}
        </>
      ) : (
        <Typography sx={{ color: C.muted, fontSize: 12.5 }}>No scored evidence was available, so a score calculation cannot be shown.</Typography>
      )}
    </Section>
  );
}

function LimitExplainability({ entity }) {
  const explanation = buildLimitExplainability(entity);
  return (
    <Section title="How the recommended limit was determined" subtitle="Decision gate, sizing inputs, and the source of the final recommendation.">
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1.5, pb: 1.5, borderBottom: `1px solid ${C.line}` }}>
        <ExplanationValue label="Requested amount" value={explanation.requestedAmount == null ? "Not supplied" : money(explanation.requestedAmount)} />
        <ExplanationValue label="Avg. monthly volume" value={explanation.monthlyVolume == null ? "Not supplied" : money(explanation.monthlyVolume)} />
        <ExplanationValue label="Risk band" value={explanation.riskBand ? label(explanation.riskBand) : "Not assigned"} />
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "160px minmax(0, 1fr)" }, gap: 1.5, py: 1.5, borderBottom: `1px solid ${C.line}` }}>
        <ExplanationValue label="Decision gate" value={explanation.decisionLabel} detail={explanation.decision === "not_approved" ? "A declined decision suppresses commercial terms." : "The recommendation passed the decision gate."} />
        <ExplanationValue label="Recommendation source" value={explanation.amountSource} detail={explanation.chosenTier ? `Selected tier: ${explanation.chosenTier}` : null} />
      </Box>
      <Box sx={{ pt: 1.5 }}>
        <Typography sx={{ color: C.muted, fontSize: 10.5, fontWeight: 850, textTransform: "uppercase" }}>Result</Typography>
        <Typography sx={{ color: explanation.decisionColor, fontSize: 23, fontWeight: 950 }}>{explanation.amount == null ? "No limit recommended" : money(explanation.amount)}</Typography>
        {explanation.holdbackPercent != null && <Typography sx={{ color: C.muted, fontSize: 11.5 }}>Recommended holdback: {Number(explanation.holdbackPercent).toLocaleString(undefined, { maximumFractionDigits: 2 })}%</Typography>}
        <Typography sx={{ color: "#41556b", mt: 1, fontSize: 12.5, lineHeight: 1.65 }}>{explanation.explanation}</Typography>
      </Box>
    </Section>
  );
}

function DecisionChecklist({ entity }) {
  const assessment = entity.assessment || {};
  const match = Math.round((entity.match?.confidence || 0) * 100);
  const missing = assessment.missingFeatures || [];
  return (
    <Section title="Decision checklist" subtitle={`${missing.length + (match < 80 ? 1 : 0)} items block completion.`}>
      <ChecklistRow title="Risk score reviewed" detail={`${assessment.score ?? "—"} · ${label(assessment.recommendation?.riskBand || "unscored")} risk band`} status="Ready" ready />
      <ChecklistRow title="Identity evidence" detail={`${match}% match confidence`} status={match >= 80 ? "Ready" : "Review"} ready={match >= 80} />
      <ChecklistRow title="Cash-flow evidence" detail={missing.length ? `${missing.length} configured items missing` : "Required evidence supplied"} status={missing.length ? "Missing" : "Ready"} ready={!missing.length} />
    </Section>
  );
}

function UnderwritingThinking({ fullUnderwriting, underwritingJob, title = "Full underwriting output" }) {
  const stage = underwritingJob?.stage || fullUnderwriting?.stage || "queued";
  const progress = underwritingJob?.progress ?? fullUnderwriting?.progress ?? 0;
  return (
    <Section
      title={title}
      subtitle="A fresh underwriting result is being generated. Previous results are hidden until this run finishes."
    >
      <Paper
        data-testid="underwriting-thinking"
        variant="outlined"
        sx={{
          p: { xs: 2.5, md: 4 },
          borderRadius: 3,
          borderColor: "#cfe0ff",
          bgcolor: "#f7faff",
          textAlign: "center",
        }}
      >
        <CircularProgress size={34} sx={{ mb: 2 }} />
        <Typography sx={{ color: C.ink, fontSize: 20, fontWeight: 950 }}>
          ArcAI is thinking…
        </Typography>
        <Typography sx={{ color: C.muted, mt: 0.75 }}>
          Reviewing company evidence and preparing a new full underwriting report.
        </Typography>
        <Chip
          sx={{ mt: 2, fontWeight: 800 }}
          color="primary"
          variant="outlined"
          label={`${label(stage)} · ${progress}% complete`}
        />
      </Paper>
    </Section>
  );
}

function UnderwritingOutput({ fullUnderwriting, underwritingJob, showEmpty = false, title = "Full underwriting output" }) {
  if (!showEmpty && fullUnderwriting?.status !== "done") return null;
  if (["queued", "running"].includes(fullUnderwriting?.status)) {
    return <UnderwritingThinking title={title} fullUnderwriting={fullUnderwriting} underwritingJob={underwritingJob} />;
  }
  const safeReport = normalizeUnderwritingMarkdown(
    clientSafeAiText(fullUnderwriting?.reportMarkdown || "Report content was not retained."),
  );
  const outline = underwritingSectionOutline(safeReport);
  const reportMeta = underwritingReportMeta(safeReport);
  return (
    <Section
      title={title}
      subtitle="The complete result generated by the configured MCA underwriting prompt."
    >
      {fullUnderwriting?.status === "error" && (
        <Alert severity="error">
          {clientSafeAiText(fullUnderwriting.message || "The underwriting job failed. Re-run it to try again.")}
        </Alert>
      )}
      {!fullUnderwriting && showEmpty && (
        <Alert severity="info">
          Refresh the report to collect external evidence and generate the full configured MCA report.
        </Alert>
      )}
      {fullUnderwriting?.status === "done" && (
        <>
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2, md: 2.5 },
              mb: 2,
              borderRadius: 3,
              borderColor: "#cfe0f5",
              background: "linear-gradient(135deg, #f8fbff 0%, #eef5ff 100%)",
              display: "flex",
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              flexDirection: { xs: "column", md: "row" },
              gap: 1.5,
            }}
          >
            <Box>
              <Typography sx={{ color: C.ink, fontSize: { xs: 20, md: 23 }, fontWeight: 950, letterSpacing: "-0.02em" }}>
                Full credit underwriting report
              </Typography>
              <Typography sx={{ color: C.muted, fontSize: 13, mt: 0.3 }}>
                Evidence review, risk analysis and supportable funding recommendation
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
              <Chip variant="outlined" label={fullUnderwriting.promptVersion || "MCA policy"} sx={{ bgcolor: "#fff" }} />
              <Chip
                variant="outlined"
                sx={{ bgcolor: "#fff" }}
                label={fullUnderwriting.generatedAt ? new Date(fullUnderwriting.generatedAt).toLocaleString() : "Generated"}
              />
            </Box>
          </Paper>
          {(reportMeta.mode || reportMeta.decision || reportMeta.confidence) && (
            <Paper
              variant="outlined"
              sx={{
                p: 1.4,
                mb: 2,
                borderColor: C.line,
                borderRadius: 2.5,
                bgcolor: "#f8fafc",
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              {reportMeta.mode && <Chip size="small" variant="outlined" label={`Mode · ${reportMeta.mode}`} />}
              {reportMeta.decision && (
                <Chip
                  size="small"
                  label={`Decision · ${reportMeta.decision}`}
                  sx={{ color: color(reportMeta.decision.toLowerCase()), bgcolor: `${color(reportMeta.decision.toLowerCase())}12`, fontWeight: 900 }}
                />
              )}
              {reportMeta.confidence && <Chip size="small" variant="outlined" label={`Confidence · ${reportMeta.confidence}`} />}
            </Paper>
          )}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: outline.length ? "220px minmax(0, 1fr)" : "1fr" }, gap: 2, alignItems: "start" }}>
            {!!outline.length && (
              <Paper
                component="nav"
                aria-label="Underwriting report sections"
                variant="outlined"
                sx={{
                  display: { xs: "flex", lg: "block" },
                  gap: 0.5,
                  p: 1,
                  borderColor: C.line,
                  borderRadius: 3,
                  overflowX: { xs: "auto", lg: "visible" },
                  position: { lg: "sticky" },
                  top: { lg: 16 },
                }}
              >
                <Typography sx={{ display: { xs: "none", lg: "block" }, px: 1, py: 0.75, color: C.muted, fontSize: 10.5, fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Report sections
                </Typography>
                {outline.map((item, index) => (
                  <Button
                    key={`${item.id}-${index}`}
                    size="small"
                    onClick={() => document.getElementById(item.id)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                    sx={{
                      width: { xs: "auto", lg: "100%" },
                      minWidth: { xs: "max-content", lg: 0 },
                      justifyContent: "flex-start",
                      textAlign: "left",
                      textTransform: "none",
                      color: C.muted,
                      fontSize: 11.5,
                      lineHeight: 1.35,
                      px: 1,
                    }}
                  >
                    <Box component="span" sx={{ color: C.blue, fontWeight: 900, mr: 0.75 }}>{String(index + 1).padStart(2, "0")}</Box>
                    {item.title}
                  </Button>
                ))}
              </Paper>
            )}
            <Paper
              variant="outlined"
              data-testid="full-underwriting-output"
              sx={{
                minWidth: 0,
                color: C.ink,
                px: { xs: 2, md: 4.5 },
                py: { xs: 2.5, md: 4 },
                borderRadius: 3,
                borderColor: C.line,
                bgcolor: "#fff",
                boxShadow: "0 18px 50px rgba(18, 48, 78, 0.07)",
                overflowWrap: "anywhere",
              }}
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={underwritingMarkdownComponents}>
                {safeReport}
              </ReactMarkdown>
            </Paper>
          </Box>
        </>
      )}
    </Section>
  );
}

function OfferLiftPanel({ offerLift, busy, invitationUrl, onRefresh, onInvite }) {
  if (!offerLift) return (
    <Section title="OfferLift" subtitle="Create an evidence-based improvement plan for this borrower.">
      <Alert severity="info">Run OfferLift from the report header to evaluate eligibility and create the requested-evidence checklist.</Alert>
    </Section>
  );
  const offer = offerLift.currentOffer || {};
  return (
    <Box>
      <Alert severity="info" sx={{ mb: 1.75, borderRadius: 2.5 }}>
        <Typography sx={{ fontWeight: 800 }}>{label(offerLift.status)}</Typography>
        {offerLift.eligibility?.explanation}
      </Alert>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(5, 1fr)" }, gap: 1.1, mb: 1.75 }}>
        <Metric title="Decision" value={label(offer.decision)} /><Metric title="Amount" value={money(offer.amount)} /><Metric title="Holdback" value={offer.holdbackPercent == null ? "—" : `${offer.holdbackPercent}%`} /><Metric title="Credit score" value={offer.creditScore ?? "—"} /><Metric title="Progress" value={`${offerLift.progress?.percent || 0}%`} />
      </Box>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.35fr) minmax(280px, 0.75fr)" }, gap: 1.75, alignItems: "start" }}>
        <Section title="Evidence collection" subtitle={`${offerLift.progress?.completed || 0} of ${(offerLift.requirements || []).length} borrower requests completed.`}>
          {(offerLift.requirements || []).map((item) => <ChecklistRow key={item.id} title={item.title} detail={item.why} status={label(item.status)} ready={item.status === "completed"} />)}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1.75 }}><Button variant="contained" disabled={busy || !offerLift.eligibility?.eligible} onClick={onInvite}>Generate secure link</Button><Button variant="outlined" disabled={busy} onClick={onRefresh}>Refresh status</Button>{invitationUrl && <Button startIcon={<ContentCopyOutlinedIcon />} onClick={() => navigator.clipboard.writeText(invitationUrl)}>Copy link</Button>}</Box>
          {invitationUrl && <TextField fullWidth size="small" value={invitationUrl} InputProps={{ readOnly: true }} sx={{ mt: 1.5 }} />}
        </Section>
        <Section title="Activity" subtitle="Auditable borrower and team events.">
          {(offerLift.activity || []).length ? (offerLift.activity || []).map((item) => <Box key={item.id} sx={{ position: "relative", pl: 2, py: 0.8, borderLeft: `1px solid ${C.line}`, "&:before": { content: '""', position: "absolute", left: -4.5, top: 15, width: 8, height: 8, borderRadius: "50%", bgcolor: "#5268ad" } }}><Typography sx={{ color: C.ink, fontSize: 12.5, fontWeight: 750 }}>{label(item.type)}</Typography><Typography sx={{ color: C.muted, fontSize: 10.5 }}>{item.actor || "System"} · {new Date(item.createdAt).toLocaleString()}</Typography></Box>) : <Typography sx={{ color: C.muted, fontSize: 12.5 }}>No activity recorded yet.</Typography>}
        </Section>
      </Box>
      {offerLift.disclaimer && <Alert severity="info" sx={{ borderRadius: 2.5 }}>{offerLift.disclaimer}</Alert>}
    </Box>
  );
}

export default function PaymentPlatformCompanyReport({ adminAction = false }) {
  const { entityId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isAdmin = useSelector((state) => state.userInfo.isAdmin);
  const currentTenantId = useSelector((state) => state.userInfo.tenantId);
  const tenantId =
    isAdmin || adminAction ? getTenantIdFromURL() : currentTenantId;
  const [entity, setEntity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState(0);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [escalationOpen, setEscalationOpen] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamMembersLoading, setTeamMembersLoading] = useState(false);
  const [teamMembersError, setTeamMembersError] = useState("");
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [underwritingJob, setUnderwritingJob] = useState(null);
  const [offerLift, setOfferLift] = useState(null);
  const [offerLiftBusy, setOfferLiftBusy] = useState(false);
  const [offerLiftInvitationUrl, setOfferLiftInvitationUrl] = useState("");
  const [offerLiftShareOpen, setOfferLiftShareOpen] = useState(false);
  const [actionsAnchor, setActionsAnchor] = useState(null);
  const [reviewForm, setReviewForm] = useState({
    decision: "approve", notes: "", amount: "", termMonths: "", factorRate: "",
  });
  const [escalationForm, setEscalationForm] = useState({ assigneeUserId: "", notes: "" });

  useEffect(() => {
    let active = true;
    if (!tenantId || !entityId) return undefined;
    setLoading(true);
    setError("");
    getPaymentPlatformEntity(tenantId, entityId)
      .then((data) => {
        if (active) setEntity(data);
      })
      .catch((requestError) => {
        if (active)
          setError(
            requestError?.response?.data?.message ||
              "Could not load the company report",
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [tenantId, entityId]);

  useEffect(() => {
    const jobId = underwritingJob?.jobId || entity?.fullUnderwriting?.jobId;
    const status = underwritingJob?.status || entity?.fullUnderwriting?.status;
    if (!tenantId || !jobId || !["queued", "running"].includes(status)) return undefined;
    let active = true;
    const poll = async () => {
      try {
        const job = await getPaymentPlatformFullUnderwritingJob(tenantId, jobId);
        if (!active) return;
        setUnderwritingJob(job);
        if (job.status === "done") {
          setEntity((current) => ({ ...current, fullUnderwriting: {
            status: "done",
            jobId,
            reportMarkdown: job.result?.reportMarkdown,
            generatedAt: job.result?.generatedAt,
            model: job.result?.model,
            providerLabel: job.result?.providerLabel,
            promptVersion: job.result?.promptVersion,
            recommendationSummary: job.result?.recommendationSummary || null,
            humanReviewRequired: true,
            evidenceDetails: {
              registrationAndUcc: job.result?.evidence?.registrationAndUcc || null,
              digitalAndSocial: job.result?.evidence?.digitalAndSocial || null,
            },
            evidenceCoverage: {
              registrationUcc: Object.values(job.result?.evidence?.registrationAndUcc || {}).some((item) => item?.status === "available"),
              dnb: job.result?.evidence?.dnb?.status === "available",
              digitalSocial: job.result?.evidence?.digitalAndSocial?.status === "available",
            },
          } }));
          toast.success("Full MCA underwriting report is ready");
        } else if (job.status === "error") {
          setEntity((current) => ({ ...current, fullUnderwriting: {
            ...(current.fullUnderwriting || {}), status: "error", message: job.message,
          } }));
          toast.error(clientSafeAiText(job.message || "Full underwriting failed"));
        }
      } catch (requestError) {
        if (active) toast.error(requestError?.response?.data?.message || "Could not refresh underwriting status");
      }
    };
    poll();
    const timer = window.setInterval(poll, 4000);
    return () => { active = false; window.clearInterval(timer); };
  }, [tenantId, entity?.fullUnderwriting?.jobId, entity?.fullUnderwriting?.status, underwritingJob?.jobId, underwritingJob?.status]);

  const assessment = entity?.assessment || {};
  const recommendation = assessment.recommendation || {};
  const headlineRecommendation = buildRecommendationPresentation(entity);
  const underwritingStatus = underwritingJob?.status || entity?.fullUnderwriting?.status;
  const underwritingInProgress = ["queued", "running"].includes(underwritingStatus);
  const hasOffer =
    assessment.decision === "approve" && recommendation.amount != null;
  const hasReportOffer =
    ["approved", "approved_with_conditions"].includes(
      headlineRecommendation.decision,
    ) && headlineRecommendation.amount != null;
  const modelValidated = assessment.validation?.status === "validated";
  const requiresManualReview =
    entity?.manualReview?.status === "completed"
      ? false
      : recommendation.requiresManualReview || !modelValidated;
  const openedFromDashboard = location.state?.reportOrigin === "dashboard";
  const backPath = openedFromDashboard
    ? `/tenant-dashboard/${tenantId}`
    : isAdmin || adminAction
      ? `/tenant-dashboard/${tenantId}/assessments`
      : "/assessments";
  const backLabel = openedFromDashboard ? "Dashboard" : "Companies";

  if (loading)
    return (
      <Box sx={{ p: 8, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  if (error || !entity)
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || "Company report not found"}</Alert>
      </Box>
    );
  const displayName =
    entity.identity?.companyName ||
    entity.identity?.externalId ||
    entity.identity?.website ||
    "Unidentified merchant";
  const approvalTermsInvalid = reviewForm.decision === "approve" && (
    Number(reviewForm.amount) <= 0 ||
    Number(reviewForm.termMonths) <= 0 ||
    Number(reviewForm.factorRate) < 1
  );
  const openReview = (decision = "approve") => {
    setReviewForm({
      decision,
      notes: "",
      amount: entity.manualReview?.amount ?? recommendation.amount ?? entity.signals?.requestedAmount ?? "",
      termMonths: entity.manualReview?.termMonths ?? recommendation.termMonths ?? 12,
      factorRate: entity.manualReview?.factorRate ?? recommendation.factorRate ?? 1.18,
    });
    setReviewOpen(true);
  };
  const openEscalation = async () => {
    setEscalationForm({ assigneeUserId: "", notes: "" });
    setTeamMembersError("");
    setEscalationOpen(true);
    setTeamMembersLoading(true);
    try {
      const result = await listQuickTaskAssignees(tenantId);
      const currentUserId = getCurrentUserId();
      setTeamMembers((result?.data || result || []).filter((member) => member.id !== currentUserId));
    } catch (requestError) {
      setTeamMembersError(requestError?.response?.data?.error || "Could not load team members");
    } finally {
      setTeamMembersLoading(false);
    }
  };
  const saveEscalation = async () => {
    const assignee = teamMembers.find((member) => member.id === escalationForm.assigneeUserId);
    if (!assignee || !escalationForm.notes.trim()) return;
    setActionBusy(true);
    try {
      await createQuickTask(tenantId, {
        companyId: entity.id || entityId,
        companyName: displayName,
        assigneeUserId: assignee.id,
        assigneeName: assignee.name,
        taskType: "approval",
        action: "Review company report",
        reason: escalationForm.notes.trim(),
      });
      setEscalationOpen(false);
      toast.success(`Report escalated to ${assignee.name}`);
    } catch (requestError) {
      toast.error(requestError?.response?.data?.error || "Could not escalate the report");
    } finally {
      setActionBusy(false);
    }
  };
  const saveReview = async () => {
    if (!reviewForm.notes.trim()) {
      toast.error("Review notes are required");
      return;
    }
    if (approvalTermsInvalid) {
      toast.error("Approved amount, term, and factor rate are required for a manual approval");
      return;
    }
    setActionBusy(true);
    try {
      const updated = await reviewPaymentPlatformEntity(tenantId, entityId, reviewForm);
      setEntity(updated);
      setReviewOpen(false);
      toast.success("Manual review recorded");
    } catch (requestError) {
      toast.error(requestError?.response?.data?.message || "Could not record the manual review");
    } finally {
      setActionBusy(false);
    }
  };
  const updateArchive = async () => {
    setActionBusy(true);
    try {
      const updated = await setPaymentPlatformEntityArchived(tenantId, entityId, !entity.archived);
      setEntity(updated);
      setArchiveOpen(false);
      toast.success(updated.archived ? "Company archived" : "Company restored");
    } catch (requestError) {
      toast.error(requestError?.response?.data?.message || "Could not update the archive status");
    } finally {
      setActionBusy(false);
    }
  };
  const refreshReport = async () => {
    const previousFullUnderwriting = entity.fullUnderwriting;
    setRefreshing(true);
    setUnderwritingJob({ status: "queued", stage: "queued", progress: 0 });
    setEntity((current) => ({
      ...current,
      fullUnderwriting: { status: "queued", stage: "queued", progress: 0 },
    }));
    setTab(1);
    try {
      const job = await startPaymentPlatformFullUnderwriting(tenantId, entityId);
      const latest = await getPaymentPlatformEntity(tenantId, entityId);
      setUnderwritingJob(job);
      setEntity({ ...latest, fullUnderwriting: job });
      setError("");
      toast.info("CrediArc is refreshing this company’s underwriting report");
    } catch (requestError) {
      setUnderwritingJob(null);
      setEntity((current) => ({ ...current, fullUnderwriting: previousFullUnderwriting }));
      toast.error(
        requestError?.response?.data?.message ||
          "Could not start the company refresh",
      );
    } finally {
      setRefreshing(false);
    }
  };

  const loadOfferLift = async () => {
    setOfferLiftBusy(true);
    try { setOfferLift(await getPaymentPlatformOfferLift(tenantId, entityId)); }
    catch (requestError) { toast.error(requestError?.response?.data?.message || "Could not load OfferLift"); }
    finally { setOfferLiftBusy(false); }
  };
  const runOfferLift = async () => {
    setOfferLiftBusy(true);
    try {
      const offerLiftCase = await startPaymentPlatformOfferLift(tenantId, entityId);
      setOfferLift(offerLiftCase);
      if (!offerLiftCase?.eligibility?.eligible) {
        setTab(5);
        toast.info("OfferLift eligibility review completed");
        return;
      }
      const result = await createPaymentPlatformOfferLiftInvitation(tenantId, entityId, { expiresInDays: 7, channel: "copy_link", baseUrl: window.location.origin });
      setOfferLiftInvitationUrl(result.url);
      setOfferLift((current) => ({ ...current, status: "invited", invitation: result.invitation }));
      setOfferLiftShareOpen(true);
      toast.success("Secure OfferLift link created");
    } catch (requestError) { toast.error(requestError?.response?.data?.message || "Could not start OfferLift"); }
    finally { setOfferLiftBusy(false); }
  };
  const createOfferLiftInvite = async () => {
    setOfferLiftBusy(true);
    try {
      const result = await createPaymentPlatformOfferLiftInvitation(tenantId, entityId, { expiresInDays: 7, channel: "copy_link", baseUrl: window.location.origin });
      setOfferLiftInvitationUrl(result.url);
      setOfferLift((current) => ({ ...current, status: "invited", invitation: result.invitation }));
      setOfferLiftShareOpen(true);
      toast.success("Secure OfferLift link created");
    } catch (requestError) { toast.error(requestError?.response?.data?.message || "Could not create OfferLift link"); }
    finally { setOfferLiftBusy(false); }
  };

  return (
    <Box sx={{ bgcolor: "#f4f6fa", minHeight: "100%", pb: 4 }}>
      <Paper square elevation={0} sx={{ bgcolor: "#fff", borderBottom: `1px solid ${C.line}` }}>
        <Box sx={{ px: { xs: 2, md: 3 }, pt: 1.5, maxWidth: 1500, mx: "auto" }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(backPath)} sx={{ mb: 1, px: 0, color: C.muted, fontSize: 12, textTransform: "none", "&:hover": { bgcolor: "transparent", color: C.blue } }}>
            {backLabel}&nbsp; / &nbsp;Company report
          </Button>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography component="h1" sx={{ color: C.ink, fontSize: { xs: 23, md: 26 }, lineHeight: 1.2, fontWeight: 850, letterSpacing: "-0.025em" }}>{displayName}</Typography>
              <Typography sx={{ color: C.muted, fontSize: 12.5, mt: 0.5 }}>
                Wix merchant · {entity.identity?.externalId || entity.identity?.registrationNumber || entity.id}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: { xs: "row", sm: "column" }, alignItems: { xs: "center", sm: "flex-end" }, gap: 0.6, flexWrap: "wrap" }}>
              <Chip
                size="small"
                label={underwritingInProgress ? "Refreshing" : entity.archived ? "Archived" : label(entity.manualReview?.status === "completed" ? entity.manualReview.decision : assessment.decision || "Unscored")}
                icon={underwritingInProgress ? <CircularProgress size={12} /> : undefined}
                sx={{ color: underwritingInProgress ? C.blue : color(entity.manualReview?.decision || assessment.decision), bgcolor: underwritingInProgress ? `${C.blue}10` : `${color(entity.manualReview?.decision || assessment.decision)}12`, fontWeight: 850 }}
              />
              {entity.fullUnderwriting?.generatedAt && !underwritingInProgress && (
                <Typography sx={{ color: C.muted, fontSize: 11.5 }}>Last refreshed {new Date(entity.fullUnderwriting.generatedAt).toLocaleDateString()}</Typography>
              )}
            </Box>
          </Box>
          {!underwritingInProgress && (
            <>
              <Box
                aria-label="Company decision summary"
                sx={{
                  mt: 2,
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "minmax(280px, 1.25fr) minmax(170px, 0.65fr) auto" },
                  alignItems: "stretch",
                  border: `1px solid ${headlineRecommendation.color}35`,
                  borderLeft: `5px solid ${headlineRecommendation.color}`,
                  borderRadius: 2.5,
                  overflow: "hidden",
                  bgcolor: `${headlineRecommendation.color}06`,
                }}
              >
                <Box sx={{ px: { xs: 2, md: 2.5 }, py: 2 }}>
                  <Typography sx={{ color: headlineRecommendation.color, fontSize: 10.5, fontWeight: 900, letterSpacing: "0.045em" }}>UNDERWRITING RECOMMENDATION</Typography>
                  <Typography sx={{ mt: 0.65, color: headlineRecommendation.color, fontSize: { xs: 25, md: 30 }, lineHeight: 1.1, fontWeight: 950, letterSpacing: "-0.02em" }}>{headlineRecommendation.label}</Typography>
                  <Typography sx={{ mt: 0.6, color: C.muted, fontSize: 12.5 }}>{requiresManualReview ? "Human action needed" : "Review complete"}</Typography>
                </Box>
                <Box sx={{ px: { xs: 2, md: 2.5 }, py: 2, borderLeft: { xs: 0, md: `1px solid ${headlineRecommendation.color}20` }, borderTop: { xs: `1px solid ${headlineRecommendation.color}18`, md: 0 }, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                  <Typography sx={{ color: C.muted, fontSize: 11.5, fontWeight: 700 }}>Recommended offer</Typography>
                  <Typography sx={{ mt: 0.4, color: C.ink, fontSize: { xs: 25, md: 30 }, lineHeight: 1.15, fontWeight: 950 }}>{headlineRecommendation.amount == null ? "—" : money(headlineRecommendation.amount)}</Typography>
                </Box>
                {!entity.archived && (
                  <Box sx={{ px: { xs: 2, md: 2.5 }, py: 2, display: "flex", alignItems: "center", justifyContent: { xs: "flex-start", md: "flex-end" }, gap: 1, flexWrap: "wrap" }}>
                    <Button variant="contained" startIcon={<RateReviewOutlinedIcon />} onClick={() => openReview("approve")} sx={{ textTransform: "none", borderRadius: 2, whiteSpace: "nowrap" }}>Approve manually</Button>
                    <Button variant="outlined" startIcon={<ForwardToInboxOutlinedIcon />} onClick={openEscalation} sx={{ textTransform: "none", borderRadius: 2, whiteSpace: "nowrap", bgcolor: "#fff" }}>Escalate to teammate</Button>
                  </Box>
                )}
              </Box>
              <Box sx={{ mt: 1.25, p: { xs: 1.5, md: 1.75 }, display: "flex", alignItems: { xs: "stretch", md: "center" }, gap: { xs: 1, md: 0 }, flexDirection: { xs: "column", md: "row" }, border: `1px solid ${C.line}`, borderRadius: 2.5, bgcolor: "#fff" }}>
                <Box sx={{ display: "flex", flex: "1 1 auto", flexWrap: { xs: "wrap", md: "nowrap" }, minWidth: 0 }}>
                  <HeaderMetric title="Credit score" value={`${headlineRecommendation.creditScore ?? "—"} / 100`} progress={headlineRecommendation.creditScore} />
                  <HeaderMetric title="Evidence coverage" value={`${assessment.coverage || 0}%`} progress={assessment.coverage || 0} />
                  <HeaderMetric title="Identity match" value={`${Math.round((entity.match?.confidence || 0) * 100)}%`} progress={Math.round((entity.match?.confidence || 0) * 100)} />
                </Box>
                <Box sx={{ pl: { md: 2 }, display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap", justifyContent: { xs: "flex-start", md: "flex-end" } }}>
                  <Button size="small" startIcon={<RefreshOutlinedIcon />} disabled={refreshing} onClick={refreshReport} sx={{ textTransform: "none", borderRadius: 2, whiteSpace: "nowrap" }}>{refreshing ? "Refreshing…" : "Refresh report"}</Button>
                  {!entity.archived && <Button size="small" startIcon={<TrendingUpOutlinedIcon />} disabled={offerLiftBusy} onClick={runOfferLift} sx={{ textTransform: "none", borderRadius: 2, whiteSpace: "nowrap" }}>{offerLiftBusy ? "Running OfferLift…" : "OfferLift"}</Button>}
                  <Button aria-label="More company actions" size="small" endIcon={<MoreHorizIcon />} onClick={(event) => setActionsAnchor(event.currentTarget)} sx={{ textTransform: "none", borderRadius: 2, whiteSpace: "nowrap" }}>Report actions</Button>
                  <Menu anchorEl={actionsAnchor} open={Boolean(actionsAnchor)} onClose={() => setActionsAnchor(null)}>
                    <MenuItem onClick={() => { setActionsAnchor(null); setArchiveOpen(true); }}><Box sx={{ color: entity.archived ? C.blue : C.amber, display: "flex", alignItems: "center" }}>{entity.archived ? <RestoreOutlinedIcon fontSize="small" /> : <ArchiveOutlinedIcon fontSize="small" />}</Box><Box component="span" sx={{ ml: 1 }}>{entity.archived ? "Restore company" : "Archive company"}</Box></MenuItem>
                  </Menu>
                </Box>
              </Box>
            </>
          )}
          <Tabs value={tab} onChange={(event, value) => setTab(value)} variant="scrollable" scrollButtons="auto" sx={{ mt: 1.25, minHeight: 44, "& .MuiTab-root": { minHeight: 44, px: 1.5, textTransform: "none", fontSize: 12.5, fontWeight: 650 }, "& .Mui-selected": { color: C.ink } }}>
            <Tab label="Decision & offer" />
            <Tab label="Full underwriting" />
            <Tab label="Identity & verification" />
            <Tab label="Applications & outcomes" />
            <Tab label="Platform & financial data" />
            <Tab label="OfferLift" onClick={() => { if (!offerLift) loadOfferLift(); }} />
          </Tabs>
        </Box>
      </Paper>
      <Box sx={{ px: { xs: 2, md: 3 }, pt: 2.25, maxWidth: 1500, mx: "auto" }}>
      {entity.manualReview?.status === "completed" && (
        <Alert severity="success" sx={{ mb: 2 }}>
          <strong>Manual review completed:</strong>{" "}
          {label(entity.manualReview.decision)} by {
            (typeof entity.manualReview.reviewedBy === "object"
              ? entity.manualReview.reviewedBy?.name || entity.manualReview.reviewedBy?.email
              : entity.manualReview.reviewedBy) || "an authorized reviewer"
          }
          {entity.manualReview.reviewedAt ? ` on ${new Date(entity.manualReview.reviewedAt).toLocaleString()}` : ""}. {entity.manualReview.notes}
        </Alert>
      )}
      {entity.archived && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This company is archived and excluded from assessment lists and portfolio totals. Restore it to record another review.
        </Alert>
      )}
        <Box>
          {tab === 0 && (
            underwritingInProgress ? (
              <UnderwritingThinking
                title="Refreshing full underwriting"
                fullUnderwriting={entity.fullUnderwriting}
                underwritingJob={underwritingJob}
              />
            ) : <>
              <TabIntro title="Decision & offer" subtitle="What the underwriter needs to decide, in one focused view." />
              {!modelValidated && (
                <Alert severity="info" sx={{ mb: 1.5, borderRadius: 2.5 }}>
                  <strong>{entity.manualReview?.status === "completed" ? "Draft model result — manual decision recorded:" : "Draft policy simulation — human review required:"}</strong>{" "}
                  this model result is not a validated prediction and must not trigger automated funding. {assessment.validation?.reason}
                </Alert>
              )}
              {!hasOffer && !hasReportOffer && (
                <Alert severity="warning" sx={{ mb: 2, borderRadius: 2.5 }}>
                  <strong>No commercial offer generated.</strong>{" "}
                  {assessment.decision === "insufficient_data" ? "Collect the missing evidence and re-run the assessment." : "Complete the required decision review before taking a funding action."}
                </Alert>
              )}
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1.45fr) minmax(280px, 0.8fr)" }, gap: 1.75, alignItems: "start" }}>
                <RecommendationSummary entity={entity} />
                <DecisionChecklist entity={entity} />
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" }, gap: 1.75, alignItems: "start" }}>
                <ScoreExplainability entity={entity} />
                <LimitExplainability entity={entity} />
              </Box>
              <Section
                title="Score drivers"
                subtitle="Every feature used in the score, including its normalized value and configured weight."
              >
                {(assessment.evidence || []).length ? (
                  <Box
                    component="table"
                    sx={{
                      width: "100%",
                      borderCollapse: "collapse",
                      "th,td": {
                        p: 1.2,
                        borderBottom: `1px solid ${C.line}`,
                        textAlign: "left",
                        fontSize: 13,
                      },
                      th: {
                        color: C.muted,
                        fontSize: 11,
                        textTransform: "uppercase",
                      },
                    }}
                  >
                    <thead>
                      <tr>
                        <th>Evidence</th>
                        <th>Normalized value</th>
                        <th>Weight</th>
                        <th>Weighted contribution</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assessment.evidence.map((item) => (
                        <tr key={item.feature}>
                          <td>
                            <strong>{label(item.feature)}</strong>
                          </td>
                          <td>{valueText(item.value)}</td>
                          <td>{item.weight}</td>
                          <td>{Number(item.value * item.weight).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Box>
                ) : (
                  <Typography sx={{ color: C.muted }}>
                    No score evidence was available.
                  </Typography>
                )}
              </Section>
              <Section title="Missing evidence">
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {(assessment.missingFeatures || []).map((feature) => (
                    <Chip
                      key={feature}
                      size="small"
                      label={label(feature)}
                      variant="outlined"
                    />
                  ))}
                  {!assessment.missingFeatures?.length && (
                    <Typography sx={{ color: C.muted }}>
                      No configured evidence is missing.
                    </Typography>
                  )}
                </Box>
              </Section>
            </>
          )}
          {tab === 1 && (
            <>
              <TabIntro title="Full underwriting" subtitle="Complete evidence review, risk analysis, and funding recommendation. Use Refresh report to generate a new result." />
              <UnderwritingOutput
                fullUnderwriting={entity.fullUnderwriting}
                underwritingJob={underwritingJob}
                showEmpty
              />
              {entity.fullUnderwriting?.evidenceCoverage && <Section title="Evidence source coverage"><FieldGrid data={entity.fullUnderwriting.evidenceCoverage} /></Section>}
              <EvidenceDetails
                title="Registration & UCC evidence"
                subtitle="Official registry results, filing rows, search status and source limitations used by the report."
                data={entity.fullUnderwriting?.evidenceDetails?.registrationAndUcc}
              />
              <DigitalSocialEvidenceSummary
                data={entity.fullUnderwriting?.evidenceDetails?.digitalAndSocial}
                reportText={entity.fullUnderwriting?.reportMarkdown}
              />
            </>
          )}
          {tab === 2 && (
            <><TabIntro title="Identity & verification" subtitle="Resolve identity gaps before using this entity in a funding decision." /><IdentityVerificationPanel entity={entity} assessment={assessment} /></>
          )}
          {tab === 3 && (
            <><TabIntro title="Applications & outcomes" subtitle="Every request, decision, funded term, and observed outcome in one auditable history." /><ApplicationsOutcomesPanel entity={entity} /></>
          )}
          {tab === 4 && (
            <>
              <TabIntro title="Platform & financial data" subtitle="Normalized Wix signals shown in decision-ready units." />
              <PlatformKpis entity={entity} />
              <Section title="Normalized signal ranges" subtitle="Human-readable view of the aggregates used by the model.">
                <SignalStatsTable stats={entity.signalStats} />
              </Section>
              <Section title="Normalized platform and financial signals"><DataRows data={entity.signals} /></Section>
              <Section title="Month-by-month performance" subtitle="Manual monthly evidence is preserved here; normalized aggregates above are used by the current model."><MonthlyPerformanceTable rows={entity.performanceHistory} /></Section>
            </>
          )}
          {tab === 5 && (
            <><TabIntro title="OfferLift" subtitle="Collect the smallest set of evidence that could support reconsideration." /><OfferLiftPanel offerLift={offerLift} busy={offerLiftBusy} invitationUrl={offerLiftInvitationUrl} onRefresh={loadOfferLift} onInvite={createOfferLiftInvite} /></>
          )}
        </Box>
      </Box>
      <Dialog open={offerLiftShareOpen} onClose={() => setOfferLiftShareOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Send OfferLift to the client</DialogTitle>
        <DialogContent dividers>
          <Alert severity="success" sx={{ mb: 2 }}>
            The secure borrower link is ready. It expires automatically and can be shared directly with the client.
          </Alert>
          <TextField
            autoFocus
            fullWidth
            label="Client link"
            value={offerLiftInvitationUrl}
            InputProps={{ readOnly: true }}
            onFocus={(event) => event.target.select()}
          />
          {offerLift?.invitation?.expiresAt && <Typography sx={{ color: C.muted, fontSize: 13, mt: 1.25 }}>
            Expires {new Date(offerLift.invitation.expiresAt).toLocaleString()}
          </Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOfferLiftShareOpen(false)}>Close</Button>
          <Button variant="outlined" disabled={!offerLiftInvitationUrl} onClick={() => window.open(offerLiftInvitationUrl, "_blank", "noopener,noreferrer")}>Open client page</Button>
          <Button variant="contained" startIcon={<ContentCopyOutlinedIcon />} disabled={!offerLiftInvitationUrl} onClick={async () => {
            await navigator.clipboard.writeText(offerLiftInvitationUrl);
            toast.success("OfferLift link copied");
          }}>Copy link</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={reviewOpen} onClose={() => !actionBusy && setReviewOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Record manual review</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This decision overrides the displayed model recommendation and is retained in the review history with the reviewer and timestamp.
          </Alert>
          <Box sx={{ display: "grid", gap: 2, pt: .5 }}>
            <TextField select label="Manual decision" value={reviewForm.decision} onChange={(event) => setReviewForm((current) => ({ ...current, decision: event.target.value }))}>
              <MenuItem value="approve">Approve</MenuItem>
              <MenuItem value="review">Keep in review</MenuItem>
              <MenuItem value="decline">Decline</MenuItem>
            </TextField>
            {reviewForm.decision === "approve" && (
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" }, gap: 1.25 }}>
                <TextField required type="number" label="Approved amount" value={reviewForm.amount} onChange={(event) => setReviewForm((current) => ({ ...current, amount: event.target.value }))} />
                <TextField required type="number" label="Term in months" value={reviewForm.termMonths} onChange={(event) => setReviewForm((current) => ({ ...current, termMonths: event.target.value }))} />
                <TextField required type="number" label="Factor rate" value={reviewForm.factorRate} inputProps={{ min: 1, step: .01 }} onChange={(event) => setReviewForm((current) => ({ ...current, factorRate: event.target.value }))} />
              </Box>
            )}
            <TextField required multiline minRows={4} label="Review notes" placeholder="Document the evidence checked and the reason for this decision." value={reviewForm.notes} onChange={(event) => setReviewForm((current) => ({ ...current, notes: event.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button disabled={actionBusy} onClick={() => setReviewOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={actionBusy || !reviewForm.notes.trim() || approvalTermsInvalid} onClick={saveReview}>
            {actionBusy ? "Saving…" : "Record decision"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={escalationOpen} onClose={() => !actionBusy && setEscalationOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Escalate report to a teammate</DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            The selected teammate will receive this company report in their task queue with your note and a link back to the review.
          </Alert>
          <Box sx={{ display: "grid", gap: 2, pt: .5 }}>
            {teamMembersError && <Alert severity="error">{teamMembersError}</Alert>}
            <TextField
              select
              required
              disabled={teamMembersLoading || Boolean(teamMembersError)}
              label="Assign to"
              value={escalationForm.assigneeUserId}
              onChange={(event) => setEscalationForm((current) => ({ ...current, assigneeUserId: event.target.value }))}
              helperText={teamMembersLoading ? "Loading team members…" : !teamMembers.length && !teamMembersError ? "No other active team members are available." : "Choose the teammate who should make the next decision."}
            >
              {teamMembers.map((member) => (
                <MenuItem key={member.id} value={member.id}>
                  {member.name}{member.email && member.email !== member.name ? ` · ${member.email}` : ""}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              required
              multiline
              minRows={4}
              label="Escalation note"
              placeholder="Explain what needs review and why you are escalating this company."
              value={escalationForm.notes}
              onChange={(event) => setEscalationForm((current) => ({ ...current, notes: event.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button disabled={actionBusy} onClick={() => setEscalationOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={actionBusy || !escalationForm.assigneeUserId || !escalationForm.notes.trim()} onClick={saveEscalation}>
            {actionBusy ? "Escalating…" : "Escalate report"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={archiveOpen} onClose={() => !actionBusy && setArchiveOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{entity.archived ? "Restore company?" : "Archive company?"}</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ color: C.muted }}>
            {entity.archived
              ? "The company will return to assessment lists and portfolio totals."
              : "The company will be removed from normal assessment lists and portfolio totals. Its report and audit history will be retained and it can be restored later."}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={actionBusy} onClick={() => setArchiveOpen(false)}>Cancel</Button>
          <Button variant="contained" color={entity.archived ? "primary" : "warning"} disabled={actionBusy} onClick={updateArchive}>
            {actionBusy ? "Saving…" : entity.archived ? "Restore company" : "Archive company"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
